import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MapPin, ChevronRight } from 'lucide-react';
import { getTowns, getOffers } from '../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import DealCard from '../components/DealCard';

const HomePage = () => {
  const navigate = useNavigate();
  const [towns, setTowns] = useState([]);
  const [featuredOffers, setFeaturedOffers] = useState([]);
  const [selectedTown, setSelectedTown] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [townsRes, offersRes] = await Promise.all([
          getTowns(),
          getOffers(null, null, true)
        ]);
        setTowns(townsRes.data);
        setFeaturedOffers(offersRes.data.slice(0, 6));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (selectedTown) params.set('town', selectedTown);
    navigate(`/deals?${params.toString()}`);
  };

  const days = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' },
  ];

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* Hero Section - background image with dark overlay */}
      <section 
        className="relative overflow-hidden bg-[#242E30] bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1463183547458-6a2c760d0912?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920')" }}
        data-testid="hero-section"
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/65" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />

        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6">
          <div className="max-w-xl py-10 lg:py-14 text-white">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-3">
              Find today's best
              <br />
              <span className="text-[#E0B0FF]">restaurant deals</span>
            </h1>
            <p className="text-base sm:text-lg text-white/85 mb-6 max-w-md">
              Discover special offers from restaurants near you. Save money on your favourite meals.
            </p>

            {/* Search Box */}
            <div 
              className="bg-white rounded-lg p-2 shadow-lg max-w-lg"
              data-testid="hero-search-box"
            >
              <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center gap-2 px-3">
                  <MapPin className="h-5 w-5 text-[#7B2CBF] shrink-0" />
                  <Select value={selectedTown} onValueChange={setSelectedTown}>
                    <SelectTrigger 
                      className="border-0 shadow-none text-[#242E30] focus:ring-0 w-full h-12 text-base"
                      data-testid="town-select"
                    >
                      <SelectValue placeholder="Enter your town" />
                    </SelectTrigger>
                    <SelectContent>
                      {towns.map((town) => (
                        <SelectItem key={town.id} value={town.id}>
                          {town.name}{town.region && `, ${town.region}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <button
                  onClick={handleSearch}
                  className="bg-[#7B2CBF] text-white hover:bg-[#5A189A] rounded-lg px-6 py-3 font-semibold transition-colors flex items-center gap-2 shrink-0"
                  data-testid="hero-search-btn"
                >
                  <Search className="h-5 w-5" />
                  <span className="hidden sm:inline">Search</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Day Filter */}
      <section className="border-b border-[#E8E8E8]" data-testid="day-filter-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 py-4 overflow-x-auto no-scrollbar">
            <span className="text-sm text-[#595959] font-medium shrink-0">Browse by day:</span>
            {days.map((day) => (
              <Link
                key={day.value}
                to={`/deals?day=${day.value}`}
                className={`px-4 py-2 rounded-full text-sm font-medium shrink-0 transition-colors ${
                  day.value === today
                    ? 'bg-[#7B2CBF] text-white'
                    : 'bg-[#F5F5F5] text-[#242E30] hover:bg-[#E8E8E8]'
                }`}
              >
                {day.label}
                {day.value === today && ' (Today)'}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Deals */}
      <section className="py-12 lg:py-16" data-testid="featured-deals-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#242E30]">
                Top deals near you
              </h2>
              <p className="text-[#595959] mt-1">
                Popular offers from local restaurants
              </p>
            </div>
            <Link
              to="/deals"
              className="hidden sm:flex items-center gap-1 text-[#7B2CBF] font-semibold hover:underline"
              data-testid="view-all-deals-btn"
            >
              View all
              <ChevronRight className="h-5 w-5" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white border border-[#E8E8E8] rounded-xl p-4 animate-pulse">
                  <div className="w-full h-40 bg-[#F5F5F5] rounded-lg mb-4" />
                  <div className="h-5 bg-[#F5F5F5] rounded w-3/4 mb-2" />
                  <div className="h-4 bg-[#F5F5F5] rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : featuredOffers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredOffers.map((offer, index) => (
                <DealCard key={offer.id || offer.restaurant_id} offer={offer} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#F5F5F5] rounded-xl">
              <p className="text-[#595959]">No deals available yet. Check back soon!</p>
            </div>
          )}

          <Link
            to="/deals"
            className="sm:hidden flex items-center justify-center gap-1 text-[#7B2CBF] font-semibold mt-6"
          >
            View all deals
            <ChevronRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* How It Works - Just Eat Style */}
      <section className="py-12 lg:py-16 bg-[#F5F5F5]" data-testid="how-it-works-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#242E30] text-center mb-4">
            How it works
          </h2>
          <p className="text-[#595959] text-center mb-12 max-w-xl mx-auto">
            Finding daily deals has never been easier
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Enter your location',
                description: 'Tell us where you are and we\'ll show you restaurants nearby.'
              },
              {
                step: '2',
                title: 'Choose a day',
                description: 'Browse deals by day of the week to plan your meals.'
              },
              {
                step: '3',
                title: 'Enjoy the savings',
                description: 'Visit the restaurant and enjoy your discounted meal.'
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="text-center opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
              >
                <div className="w-14 h-14 bg-[#7B2CBF] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">{item.step}</span>
                </div>
                <h3 className="text-lg font-bold text-[#242E30] mb-2">{item.title}</h3>
                <p className="text-[#595959] text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Restaurant CTA */}
      <section className="py-12 lg:py-16" data-testid="cta-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <div className="bg-[#242E30] rounded-2xl p-8 sm:p-12 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                Own a restaurant?
              </h2>
              <p className="text-white/70 max-w-md">
                Join Zoopbowl for free and attract more customers with your daily specials.
              </p>
            </div>
            <Link
              to="/register?role=restaurant"
              className="bg-[#7B2CBF] text-white hover:bg-[#5A189A] px-8 py-4 font-semibold rounded-lg transition-colors flex items-center gap-2 shrink-0"
              data-testid="restaurant-cta-btn"
            >
              Get started
              <ChevronRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
